import { useMemo } from "react";
import type * as GeoJSON from "geojson";

import { SceneLayerEntry, StoryLayerKey, StoryMapData } from "../../types/storyMap";
import {
  StoryInspectRequestFor,
  storyInspectRequestKey,
  useStoryCustomLayerFeaturesByProject,
  useStoryCustomLayersByProject,
  useStoryIgnitionRegimesByProject,
  useStoryInspectFootprintsByProject,
  useStorySimResultsByProject,
  useStrategyTreatmentsByProject,
  regimeToFeatureCollection,
} from "../../api/storyMaps";
import {
  ASSETS_PAINT,
  BOUNDARY_LINE_PAINT,
  CUSTOM_LAYERS_FILL_PAINT,
  CUSTOM_LAYERS_LINE_PAINT,
  CUSTOM_LAYERS_POINT_PAINT,
  FIRE_HISTORY_PAINT,
  FREEHAND_FILL_PAINT,
  FREEHAND_LINE_LAYOUT,
  FREEHAND_LINE_PAINT,
  FREEHAND_STYLE_FILTERS,
  FUEL_LOAD_PAINT,
  GEOM_LINES,
  GEOM_POINTS,
  GEOM_POLYGONS,
  IGNITIONS_FILL_PAINT,
  IGNITIONS_LINE_PAINT,
  IGNITIONS_POINT_PAINT,
  INSPECTION_FILL_PAINT,
  PROPERTIES_LINE_PAINT,
  RISK_FROM_FILL_PAINT,
  RISK_FROM_LINE_PAINT,
  RISK_FROM_POINT_PAINT,
  TREATMENTS_FILL_PAINT,
  TREATMENTS_LINE_PAINT,
  VEGETATION_PAINT,
  filterTreatmentsByType,
  mergeCustomLayerFeatures,
  CirclePaint,
  FillPaint,
  LinePaint,
  StoryCanvasLayer,
} from "./storyLayers";
import {
  styledCirclePaint,
  styledFillPaint,
  styledLinePaint,
} from "./layerStyles";
import {
  resolveWeatherTimestamp,
  sampleWeather,
  Bbox,
  WEATHER_ARROW_IMAGE_ID,
  WEATHER_LABEL_LAYOUT,
  WEATHER_LABEL_PAINT,
  weatherLabelGeojson,
  weatherPanelFrom,
  WeatherPanelData,
  WeatherSample,
} from "./storyWeather";
import { UnitSystem } from "../../utils/units";
import { buildUrbanArrowImage } from "./storyLayers";
import { WindField } from "../../types/weatherGrid";
import {
  buildInspectionGeojson,
  buildRiskFromGeojson,
  buildRiskToImage,
  ensembleInspectionVisibility,
  footprintContains,
  simLayerFromFrozen,
  simLayerFromResults,
  SimLayerData,
} from "./simLayerData";

/**
 * Whether sim-layer data is an ensemble's. The frozen bundle says so
 * explicitly; live collated results are recognised by their missing
 * per-ignition footprints (collation strips them for ensembles only).
 */
function dataIsEnsemble(data: SimLayerData): boolean {
  if (data.isEnsemble != null) return data.isEnsemble;
  return data.ignitions.length > 0 && data.ignitions.every((i) => !i.finalFrame);
}
import { entryProjectId, SIM_ENTRY_KEYS } from "./layerEntries";

/** Inclusive EndDate-year filter for treatments; undated features stay. */
export function filterTreatmentsByYearRange(
  fc: GeoJSON.FeatureCollection | null,
  range: [number | null, number | null] | undefined
): GeoJSON.FeatureCollection | null {
  if (!fc || !range || (range[0] == null && range[1] == null)) return fc;
  const [from, to] = range;
  return {
    type: "FeatureCollection",
    features: fc.features.filter((f) => {
      const raw = f?.properties?.EndDate ?? f?.properties?.endDate;
      if (!raw) return true;
      const year = new Date(raw).getFullYear();
      if (!Number.isFinite(year)) return true;
      return (from == null || year >= from) && (to == null || year <= to);
    }),
  };
}

/** Style metadata + features for one custom layer (frozen or live). */
export interface CustomLayerResource {
  name?: string | null;
  color?: string | null;
  opacity?: number | null;
  features: GeoJSON.FeatureCollection;
}

/**
 * One project's page-level map data: what its boundary / properties / fire
 * history / assets entries draw, and the bbox weather layers lay out over.
 */
export interface ProjectPageResources {
  boundary: GeoJSON.Feature | null;
  properties: GeoJSON.FeatureCollection | null;
  /** Already decorated (decorateFireHistory). */
  fireHistory: GeoJSON.FeatureCollection | null;
  assetsTilesUrl: string | null;
  bbox: Bbox | null;
}

/**
 * Everything buildEntryLayers needs, resolved by useEntryLayerStack (frozen
 * mapData when public, live queries when authed).
 *
 * The flat `boundary` / `properties` / `fireHistory` / `assetsTilesUrl` /
 * `weatherBbox` fields are the story's own project's (project stories, and
 * stories published before folder stories existed). `pages` holds the same
 * per cited project for folder and organisation-wide stories; an entry reads
 * its own project's page and falls back to the flat fields.
 */
export interface EntryLayerResources {
  boundary?: GeoJSON.Feature | null;
  properties?: GeoJSON.FeatureCollection | null;
  /** Already decorated (decorateFireHistory). */
  fireHistory?: GeoJSON.FeatureCollection | null;
  assetsTilesUrl?: string | null;
  /** Project stories: the project every entry implicitly cites. */
  defaultProjectId?: number | null;
  /** Page-level data per cited project, keyed by project id. */
  pages?: Record<string, ProjectPageResources>;
  /** Project id → name (legend rows in multi-project stacks). */
  projectNames?: Record<string, string>;
  treatmentsByStrategy?: Record<string, GeoJSON.FeatureCollection>;
  ignitionsByRegime?: Record<string, GeoJSON.FeatureCollection>;
  customLayersById?: Record<string, CustomLayerResource>;
  simDataById?: Record<string, SimLayerData>;
  /** Frozen inspection visibility keyed by entry id (public viewers). */
  inspectionByEntry?: Record<string, (boolean | null)[]>;
  /**
   * Animation-scene playback position (seconds since simulation start);
   * weather entries follow it instead of their fixed hour. Null = not
   * animating.
   */
  weatherElapsedSeconds?: number | null;
  /** Project bbox, for uniform-weather label placement + particle bounds. */
  weatherBbox?: Bbox | null;
  unitSystem?: UnitSystem;
}

/** Wind the particle overlay should animate (first weather entry showing wind). */
export interface WindOverlay {
  windSpeed: number;
  windDirection: number;
  field: WindField | null;
}

/** The page-level data an entry draws from: its project's, else the story's own. */
export function pageFor(
  entry: { projectId?: number | null },
  resources: EntryLayerResources
): ProjectPageResources {
  const pid = entryProjectId(entry, resources.defaultProjectId ?? null);
  const page = pid !== null ? resources.pages?.[String(pid)] : undefined;
  if (page) return page;
  return {
    boundary: resources.boundary ?? null,
    properties: resources.properties ?? null,
    fireHistory: resources.fireHistory ?? null,
    assetsTilesUrl: resources.assetsTilesUrl ?? null,
    bbox: resources.weatherBbox ?? null,
  };
}

/** The hour a weather entry resolves to, sampled — shared by layers + particles. */
function weatherSampleFor(
  entry: SceneLayerEntry,
  resources: EntryLayerResources
): WeatherSample | null {
  const data = entry.simulationId
    ? resources.simDataById?.[entry.simulationId]?.weather ?? null
    : null;
  if (!data) return null;
  const timestamp = resolveWeatherTimestamp(
    data,
    entry.weatherTime,
    resources.weatherElapsedSeconds
  );
  return sampleWeather(data, timestamp, pageFor(entry, resources).bbox);
}

/** The readings panel: first weather entry with the panel on. */
export interface WeatherPanel extends WeatherPanelData {
  side: "left" | "right";
}

export function buildWeatherPanel(
  entries: SceneLayerEntry[],
  resources: EntryLayerResources
): WeatherPanel | null {
  for (const entry of entries) {
    if (entry.key !== "weather" || entry.weatherShowPanel === false) continue;
    const data = entry.simulationId
      ? resources.simDataById?.[entry.simulationId]?.weather ?? null
      : null;
    const panel = weatherPanelFrom(weatherSampleFor(entry, resources), data);
    if (panel) return { ...panel, side: entry.weatherPanelSide ?? "right" };
  }
  return null;
}

/** Pure: which wind the particle layer should draw for these entries. */
export function buildWindOverlay(
  entries: SceneLayerEntry[],
  resources: EntryLayerResources
): WindOverlay | null {
  for (const entry of entries) {
    if (entry.key !== "weather" || entry.weatherShowWind === false) continue;
    const sample = weatherSampleFor(entry, resources);
    if (sample) {
      return {
        windSpeed: sample.windSpeed,
        windDirection: sample.windDirection,
        field: sample.field,
      };
    }
  }
  return null;
}

/** Bottom→top z-order; entries stack by key, then by list order within a key. */
const KEY_Z_ORDER: StoryLayerKey[] = [
  "fuel_3d",
  "vegetation",
  "fuel_load",
  "risk_to",
  "fire_history",
  "custom_layers",
  "treatments",
  "assets",
  "risk_from",
  "inspection",
  "ignitions",
  "properties",
  "boundary",
  "freehand",
  "weather",
];

/** Empty collection for freehand entries with nothing drawn yet. */
const EMPTY_FC: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };

/**
 * Freehand annotations: one fill layer for polygons plus three line layers
 * (solid / dashed / dotted) covering polygon outlines and linestrings — a
 * dash array can't vary per feature, so lineStyle selects the layer.
 */
function pushFreehandLayers(
  layers: StoryCanvasLayer[],
  base: string,
  data: GeoJSON.FeatureCollection
) {
  layers.push({
    id: `${base}-fill`,
    kind: "geojson-fill",
    data,
    fillPaint: FREEHAND_FILL_PAINT,
    filter: GEOM_POLYGONS,
    visible: true,
  });
  (["solid", "dashed", "dotted"] as const).forEach((lineStyle) => {
    const paint: LinePaint = { ...FREEHAND_LINE_PAINT };
    if (lineStyle === "dashed") paint["line-dasharray"] = [2, 2];
    if (lineStyle === "dotted") paint["line-dasharray"] = [0, 2];
    layers.push({
      id: `${base}-${lineStyle}`,
      kind: "geojson-line",
      data,
      paint,
      layout: FREEHAND_LINE_LAYOUT,
      filter: FREEHAND_STYLE_FILTERS[lineStyle],
      visible: true,
    });
  });
}

/**
 * Turn configured layer entries into the declarative stack StoryMapCanvas
 * renders. Pure: all data arrives via `resources`. Layer ids embed the entry
 * id, so several entries of one key (e.g. two strategies' treatments) coexist.
 */
export function buildEntryLayers(
  entries: SceneLayerEntry[],
  idPrefix: string,
  resources: EntryLayerResources
): StoryCanvasLayer[] {
  const layers: StoryCanvasLayer[] = [];

  // Polygon / line / point triple over one source, each paint folded with
  // the entry's style. `allowColor` false keeps per-feature colour ramps.
  const pushGeomSplit = (
    base: string,
    data: GeoJSON.FeatureCollection | null,
    paints: { fill: FillPaint; line: LinePaint; point: CirclePaint },
    entry: SceneLayerEntry,
    opts: { allowColor: boolean; patternFallbackColor?: string }
  ) => {
    const fill = styledFillPaint(paints.fill, entry.style, opts);
    const line = styledLinePaint(paints.line, entry.style, opts);
    layers.push({
      id: `${base}-fill`,
      kind: "geojson-fill",
      data,
      fillPaint: fill.paint,
      pattern: fill.pattern ?? undefined,
      // Patterned polygons keep a crisp edge: outline in the line style.
      linePaint: fill.pattern ? line.paint : undefined,
      lineLayout: fill.pattern ? line.layout : undefined,
      filter: GEOM_POLYGONS,
      visible: true,
    });
    layers.push({
      id: `${base}-lines`,
      kind: "geojson-line",
      data,
      paint: line.paint,
      layout: line.layout,
      filter: GEOM_LINES,
      visible: true,
    });
    layers.push({
      id: `${base}-points`,
      kind: "geojson-circle",
      data,
      paint: styledCirclePaint(paints.point, entry.style, opts),
      filter: GEOM_POINTS,
      visible: true,
    });
  };

  KEY_Z_ORDER.forEach((key) => {
    entries
      .filter((entry) => entry.key === key)
      .forEach((entry) => {
        const base = `${idPrefix}-${entry.id}`;
        const simData =
          entry.simulationId != null
            ? resources.simDataById?.[entry.simulationId] ?? null
            : null;
        switch (key) {
          case "vegetation": {
            if (!entry.tiles?.vegetationTilesUrl) break;
            layers.push({
              id: base,
              kind: "vector",
              url: entry.tiles.vegetationTilesUrl,
              sourceLayer: "vegetation",
              paint: styledFillPaint(VEGETATION_PAINT, entry.style, {
                allowColor: false,
              }).paint,
              minzoom: 5,
              maxzoom: 12,
              tileSize: 512,
              promoteId: "__id",
              visible: true,
            });
            break;
          }
          case "fuel_load": {
            if (!entry.tiles?.fuelTilesUrl) break;
            layers.push({
              id: base,
              kind: "vector",
              url: entry.tiles.fuelTilesUrl,
              sourceLayer: "fuel_load",
              paint: styledFillPaint(FUEL_LOAD_PAINT, entry.style, {
                allowColor: false,
              }).paint,
              minzoom: 6,
              maxzoom: 12,
              tileSize: 512,
              promoteId: "__id",
              visible: true,
            });
            break;
          }
          case "fuel_3d": {
            // Invisible fill whose only job is to load the fuel tiles: the
            // three.js tree layer (StoryFuel3DLayers, mounted by the page
            // from buildFuel3DItems) queries this source's features. Same
            // arrangement as the project page's standalone 3D Fuel layer.
            if (!entry.tiles?.fuelTilesUrl) break;
            layers.push({
              id: base,
              kind: "vector",
              url: entry.tiles.fuelTilesUrl,
              sourceLayer: "fuel_load",
              paint: { "fill-opacity": 0 },
              minzoom: 6,
              maxzoom: 12,
              tileSize: 512,
              promoteId: "__id",
              visible: true,
            });
            break;
          }
          case "risk_to": {
            const image = buildRiskToImage(simData, pageFor(entry, resources).boundary);
            if (!image) break;
            layers.push({
              id: base,
              kind: "image",
              url: image.url,
              coordinates: image.coordinates,
              opacity: entry.style?.opacity ?? 0.6,
              visible: true,
            });
            break;
          }
          case "fire_history": {
            layers.push({
              id: base,
              kind: "geojson-fill",
              data: pageFor(entry, resources).fireHistory,
              fillPaint: styledFillPaint(FIRE_HISTORY_PAINT, entry.style, {
                allowColor: false,
              }).paint,
              visible: true,
            });
            break;
          }
          case "custom_layers": {
            const merged = mergeCustomLayerFeatures(
              (entry.customLayerIds ?? [])
                .map((id) => resources.customLayersById?.[String(id)])
                .filter((l): l is CustomLayerResource => !!l)
            );
            pushGeomSplit(
              base,
              merged,
              {
                fill: CUSTOM_LAYERS_FILL_PAINT,
                line: CUSTOM_LAYERS_LINE_PAINT,
                point: CUSTOM_LAYERS_POINT_PAINT,
              },
              entry,
              // Custom layers carry their own colour; an explicit override
              // wins, and a pattern falls back to the first layer's colour.
              {
                allowColor: true,
                patternFallbackColor:
                  (entry.customLayerIds ?? [])
                    .map((id) => resources.customLayersById?.[String(id)]?.color)
                    .find((c): c is string => typeof c === "string") ??
                  "#1abc9c",
              }
            );
            break;
          }
          case "treatments": {
            const raw =
              entry.strategyId != null
                ? resources.treatmentsByStrategy?.[String(entry.strategyId)] ??
                  null
                : null;
            const data = filterTreatmentsByYearRange(
              filterTreatmentsByType(raw, entry.treatmentTypes),
              entry.treatmentYearRange
            );
            {
              const fill = styledFillPaint(TREATMENTS_FILL_PAINT, entry.style, {
                allowColor: true,
                patternFallbackColor: "#ffb74d",
              });
              const line = styledLinePaint(TREATMENTS_LINE_PAINT, entry.style, {
                allowColor: true,
              });
              layers.push({
                id: base,
                kind: "geojson-fill",
                data,
                fillPaint: fill.paint,
                pattern: fill.pattern ?? undefined,
                linePaint: line.paint,
                lineLayout: line.layout,
                visible: true,
              });
            }
            break;
          }
          case "assets": {
            const assetsTilesUrl = pageFor(entry, resources).assetsTilesUrl;
            if (!assetsTilesUrl) break;
            layers.push({
              id: base,
              kind: "vector",
              url: assetsTilesUrl,
              sourceLayer: "assets",
              ...(() => {
                const fill = styledFillPaint(ASSETS_PAINT, entry.style, {
                  allowColor: true,
                  patternFallbackColor: "#aaff00",
                });
                return { paint: fill.paint, pattern: fill.pattern ?? undefined };
              })(),
              minzoom: 5,
              maxzoom: 12,
              tileSize: 512,
              promoteId: "__id",
              visible: true,
            });
            break;
          }
          case "risk_from": {
            pushGeomSplit(
              base,
              buildRiskFromGeojson(simData, entry.riskFromMetric ?? "fire_size", resources.unitSystem),
              {
                fill: RISK_FROM_FILL_PAINT,
                line: RISK_FROM_LINE_PAINT,
                point: RISK_FROM_POINT_PAINT,
              },
              entry,
              { allowColor: false }
            );
            break;
          }
          case "inspection": {
            layers.push({
              id: base,
              kind: "geojson-fill",
              data: buildInspectionGeojson(
                simData,
                entry.inspectionPoint,
                resources.inspectionByEntry?.[entry.id]
              ),
              fillPaint: styledFillPaint(INSPECTION_FILL_PAINT, entry.style, {
                allowColor: false,
              }).paint,
              visible: true,
            });
            break;
          }
          case "ignitions": {
            const data =
              entry.ignitionRegimeId != null
                ? resources.ignitionsByRegime?.[
                    String(entry.ignitionRegimeId)
                  ] ?? null
                : null;
            pushGeomSplit(
              base,
              data,
              {
                fill: IGNITIONS_FILL_PAINT,
                line: IGNITIONS_LINE_PAINT,
                point: IGNITIONS_POINT_PAINT,
              },
              entry,
              { allowColor: true, patternFallbackColor: "#73ff00" }
            );
            break;
          }
          case "properties": {
            {
              const line = styledLinePaint(PROPERTIES_LINE_PAINT, entry.style, {
                allowColor: true,
              });
              layers.push({
                id: base,
                kind: "geojson-line",
                data: pageFor(entry, resources).properties,
                paint: line.paint,
                layout: line.layout,
                visible: true,
              });
            }
            break;
          }
          case "boundary": {
            {
              const line = styledLinePaint(BOUNDARY_LINE_PAINT, entry.style, {
                allowColor: true,
              });
              layers.push({
                id: base,
                kind: "geojson-line",
                data: pageFor(entry, resources).boundary,
                paint: line.paint,
                layout: line.layout,
                visible: true,
              });
            }
            break;
          }
          case "freehand": {
            pushFreehandLayers(layers, base, entry.features ?? EMPTY_FC);
            break;
          }
          case "weather": {
            if (entry.weatherShowLabels === false) break;
            const sample = weatherSampleFor(entry, resources);
            const arrow = buildUrbanArrowImage();
            layers.push({
              id: `${base}-labels`,
              kind: "geojson-symbol",
              data: weatherLabelGeojson(sample, resources.unitSystem),
              paint: WEATHER_LABEL_PAINT,
              layout: WEATHER_LABEL_LAYOUT,
              image: arrow ? { id: WEATHER_ARROW_IMAGE_ID, data: arrow, sdf: true } : undefined,
              visible: true,
            });
            break;
          }
        }
      });
  });

  return layers;
}

/** One "fuel_3d" entry the page mounts a three.js tree layer for. */
export interface Fuel3DItem {
  /** The entry's id (React key). */
  id: string;
  /** Mapbox source id of the entry's invisible fuel-tile loader. */
  sourceId: string;
  /** The project whose imagery-placed crowns to draw; null when unknown. */
  projectId: number | null;
  show: { canopy: boolean; stems: boolean; surface: boolean; grass: boolean };
  /** Lay the basemap-muting film under the stand. */
  dimBasemap: boolean;
}

/**
 * Pure: the 3D fuel entries with tiles to draw from. Source ids match the
 * invisible loader layers buildEntryLayers declares for the same entries.
 */
export function buildFuel3DItems(
  entries: SceneLayerEntry[],
  idPrefix: string,
  resources: EntryLayerResources
): Fuel3DItem[] {
  const items: Fuel3DItem[] = [];
  entries.forEach((entry) => {
    if (entry.key !== "fuel_3d" || !entry.tiles?.fuelTilesUrl) return;
    items.push({
      id: entry.id,
      sourceId: `${idPrefix}-${entry.id}`,
      projectId: entryProjectId(entry, resources.defaultProjectId ?? null),
      show: {
        canopy: entry.fuel3dCanopy !== false,
        stems: entry.fuel3dStems !== false,
        surface: entry.fuel3dSurface !== false,
        grass: entry.fuel3dGrass !== false,
      },
      dimBasemap: entry.fuel3dDimBasemap !== false,
    });
  });
  return items;
}

export interface EntryLayerStack {
  layers: StoryCanvasLayer[];
  /** True once every data fetch the entries depend on has settled. */
  settled: boolean;
  /** Resolved data the layers were built from (legend labels/colours). */
  resources: EntryLayerResources;
  /** Wind for the particle overlay, when a weather entry asks for it. */
  wind: WindOverlay | null;
  /** Readings for the weather side panel, when a weather entry asks for it. */
  weatherPanel: WeatherPanel | null;
  /** 3D fuel stands to mount (StoryFuel3DLayers), one per fuel_3d entry. */
  fuel3d: Fuel3DItem[];
}

const NO_PAGES: Record<string, ProjectPageResources> = {};

/**
 * Data resolution for a list of configured layer entries — one hook for
 * scenes, compare sides, the builder and the print pipeline. Public readers
 * resolve everything from the frozen mapData bundle; authed modes fetch live,
 * each entry against the project it cites (its own, or the story's).
 * Page-level resources (boundary, properties, fire history, assets tiles)
 * come from the caller so they're fetched once per page, not per stack.
 */
export function useEntryLayerStack({
  entries,
  isPublic,
  defaultProjectId = null,
  idPrefix,
  mapData,
  boundary,
  properties,
  fireHistory,
  assetsTilesUrl,
  pages = NO_PAGES,
  projectNames,
  pageSourcesSettled = true,
  weatherElapsedSeconds = null,
  weatherBbox = null,
  unitSystem = "metric",
}: {
  entries: SceneLayerEntry[];
  isPublic: boolean;
  /** Project stories: the project entries without a projectId cite. */
  defaultProjectId?: number | null;
  idPrefix: string;
  mapData: StoryMapData | null | undefined;
  boundary?: GeoJSON.Feature | null;
  properties?: GeoJSON.FeatureCollection | null;
  fireHistory?: GeoJSON.FeatureCollection | null;
  assetsTilesUrl?: string | null;
  /** Page-level data per cited project (folder / organisation-wide stories). */
  pages?: Record<string, ProjectPageResources>;
  projectNames?: Record<string, string>;
  /** Caller's settled flag for the page-level resources above. */
  pageSourcesSettled?: boolean;
  /** Animation playback position for weather entries (null = fixed hour). */
  weatherElapsedSeconds?: number | null;
  weatherBbox?: Bbox | null;
  unitSystem?: UnitSystem;
}): EntryLayerStack {
  // Each live request names the project the entry draws from; entries whose
  // project is unknown (a folder story's entry the author has not assigned
  // yet) make no request and draw nothing.
  const pidOf = (entry: SceneLayerEntry) => entryProjectId(entry, defaultProjectId);
  const treatmentRequests = useMemo(() => {
    const seen = new Set<string>();
    const out: { projectId: number; strategyId: number }[] = [];
    entries.forEach((e) => {
      const pid = pidOf(e);
      if (e.key !== "treatments" || e.strategyId == null || pid === null) return;
      const key = `${pid}:${e.strategyId}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ projectId: pid, strategyId: e.strategyId });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, defaultProjectId]);
  const regimeProjectIds = useMemo(
    () =>
      Array.from(
        new Set(
          entries
            .filter((e) => e.key === "ignitions" && e.ignitionRegimeId != null)
            .map(pidOf)
            .filter((p): p is number => p !== null)
        )
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entries, defaultProjectId]
  );
  const customRequests = useMemo(() => {
    const seen = new Set<string>();
    const out: { projectId: number; layerId: number }[] = [];
    entries.forEach((e) => {
      const pid = pidOf(e);
      if (e.key !== "custom_layers" || pid === null) return;
      (e.customLayerIds ?? []).forEach((layerId) => {
        const key = `${pid}:${layerId}`;
        if (seen.has(key)) return;
        seen.add(key);
        out.push({ projectId: pid, layerId });
      });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, defaultProjectId]);
  const customProjectIds = useMemo(
    () => Array.from(new Set(customRequests.map((r) => r.projectId))),
    [customRequests]
  );
  const customIds = useMemo(
    () => Array.from(new Set(customRequests.map((r) => r.layerId))),
    [customRequests]
  );
  const simRequests = useMemo(() => {
    const seen = new Set<string>();
    const out: { projectId: number; simulationId: string }[] = [];
    entries.forEach((e) => {
      const pid = pidOf(e);
      if (!SIM_ENTRY_KEYS.includes(e.key) || e.simulationId == null || pid === null) return;
      const key = `${pid}:${e.simulationId}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ projectId: pid, simulationId: e.simulationId });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, defaultProjectId]);
  const simIds = useMemo(() => Array.from(new Set(simRequests.map((r) => r.simulationId))), [simRequests]);
  const simProjectOf = useMemo(() => {
    const out: Record<string, number> = {};
    simRequests.forEach((r) => {
      out[r.simulationId] = r.projectId;
    });
    return out;
  }, [simRequests]);

  const treatmentsQuery = useStrategyTreatmentsByProject(isPublic ? [] : treatmentRequests, !isPublic);
  const regimesQuery = useStoryIgnitionRegimesByProject(isPublic ? [] : regimeProjectIds, !isPublic);
  const customListQuery = useStoryCustomLayersByProject(isPublic ? [] : customProjectIds, !isPublic);
  const customFeaturesQuery = useStoryCustomLayerFeaturesByProject(isPublic ? [] : customRequests, !isPublic);
  const simQuery = useStorySimResultsByProject(isPublic ? [] : simRequests, !isPublic);
  const regimeNeeded = regimeProjectIds.length > 0;

  const simDataById = useMemo<Record<string, SimLayerData>>(() => {
    const out: Record<string, SimLayerData> = {};
    if (isPublic) {
      Object.entries(mapData?.simLayers ?? {}).forEach(([id, frozen]) => {
        const data = simLayerFromFrozen(frozen);
        if (data) out[id] = data;
      });
    } else {
      simIds.forEach((id) => {
        const data = simLayerFromResults(simQuery.resultsBySim[id]);
        if (data) out[id] = data;
      });
    }
    return out;
  }, [isPublic, mapData?.simLayers, simIds, simQuery.resultsBySim]);

  // Ensemble inspection entries have no client-side footprints to hit-test —
  // fetch each cited (simulation, view)'s per-base-point footprints ONCE, so
  // hit-tests against the entry's inspection point run locally and instantly.
  const footprintRequests = useMemo<StoryInspectRequestFor[]>(() => {
    if (isPublic) return [];
    const seen = new Set<string>();
    const requests: StoryInspectRequestFor[] = [];
    entries.forEach((entry) => {
      if (entry.key !== "inspection" || !entry.simulationId) return;
      const projectId = simProjectOf[entry.simulationId];
      if (projectId === undefined) return;
      const data = simDataById[entry.simulationId];
      if (!data || !dataIsEnsemble(data)) return;
      const percentileScoped = entry.ensembleView === "percentile";
      if (percentileScoped && entry.resolvedMember == null) return;
      const request: StoryInspectRequestFor = {
        projectId,
        simulationId: entry.simulationId,
        member: percentileScoped ? entry.resolvedMember ?? null : null,
      };
      const key = storyInspectRequestKey(request);
      if (seen.has(key)) return;
      seen.add(key);
      requests.push(request);
    });
    return requests;
  }, [isPublic, entries, simDataById, simProjectOf]);
  const footprintsQuery = useStoryInspectFootprintsByProject(
    footprintRequests,
    !isPublic && footprintRequests.length > 0
  );

  const inspectionByEntry = useMemo<
    Record<string, (boolean | null)[]> | undefined
  >(() => {
    if (isPublic) return mapData?.inspection;
    const out: Record<string, (boolean | null)[]> = {};
    entries.forEach((entry) => {
      if (entry.key !== "inspection" || !entry.simulationId) return;
      const data = simDataById[entry.simulationId];
      if (!data || !dataIsEnsemble(data)) return; // single runs hit-test frames
      const percentileScoped = entry.ensembleView === "percentile";
      const member = percentileScoped ? entry.resolvedMember ?? null : null;
      if (!entry.inspectionPoint) {
        // No point picked: every origin shows.
        out[entry.id] = ensembleInspectionVisibility(data, 1, null, null);
        return;
      }
      const answer =
        percentileScoped && member == null
          ? undefined // percentile not resolved yet
          : footprintsQuery.byRequest[
              storyInspectRequestKey({
                simulationId: entry.simulationId,
                member,
              })
            ];
      if (!answer) {
        // Loading / unresolved percentile / lookup failed: draw nothing yet.
        out[entry.id] = data.ignitions.map(() => null);
        return;
      }
      const point = entry.inspectionPoint;
      const impactedByPoint = new Map<number, boolean | null>(
        answer.footprints.map((f) => [
          f.point,
          f.footprint ? footprintContains(f.footprint, point) : null,
        ])
      );
      out[entry.id] = ensembleInspectionVisibility(
        data,
        answer.points_per_member,
        answer.mode === "member" ? member : null,
        impactedByPoint
      );
    });
    return out;
  }, [
    isPublic,
    mapData?.inspection,
    entries,
    simDataById,
    footprintsQuery.byRequest,
  ]);

  const resources = useMemo<EntryLayerResources>(() => {
    const treatmentsByStrategy = isPublic
      ? mapData?.treatments ?? {}
      : treatmentsQuery.treatmentsByStrategy;

    let ignitionsByRegime: Record<string, GeoJSON.FeatureCollection> = {};
    if (isPublic) {
      ignitionsByRegime = mapData?.ignitionRegimes ?? {};
    } else {
      Object.values(regimesQuery.regimesByProject).forEach((regimes) =>
        regimes.forEach((regime) => {
          const fc = regimeToFeatureCollection(regime);
          if (fc) ignitionsByRegime[String(regime.id)] = fc;
        })
      );
    }

    const customLayersById: Record<string, CustomLayerResource> = {};
    if (isPublic) {
      Object.entries(mapData?.customLayers ?? {}).forEach(([id, layer]) => {
        customLayersById[id] = {
          name: layer.name,
          color: layer.color,
          opacity: layer.opacity,
          features: layer.features,
        };
      });
    } else {
      customIds.forEach((id) => {
        const features = customFeaturesQuery.featuresById[id];
        if (!features) return;
        const meta = Object.values(customListQuery.layersByProject)
          .flat()
          .find((l) => l.id === id);
        customLayersById[String(id)] = {
          name: meta?.name,
          color: meta?.color,
          opacity: meta?.opacity,
          features,
        };
      });
    }

    return {
      boundary,
      properties,
      fireHistory,
      assetsTilesUrl,
      defaultProjectId,
      pages,
      projectNames,
      treatmentsByStrategy,
      ignitionsByRegime,
      customLayersById,
      simDataById,
      inspectionByEntry,
      weatherElapsedSeconds,
      weatherBbox,
      unitSystem,
    };
  }, [
    isPublic,
    mapData,
    boundary,
    properties,
    fireHistory,
    assetsTilesUrl,
    defaultProjectId,
    pages,
    projectNames,
    treatmentsQuery.treatmentsByStrategy,
    regimesQuery.regimesByProject,
    customIds,
    customListQuery.layersByProject,
    customFeaturesQuery.featuresById,
    simDataById,
    inspectionByEntry,
    weatherElapsedSeconds,
    weatherBbox,
    unitSystem,
  ]);

  const layers = useMemo(
    () => buildEntryLayers(entries, idPrefix, resources),
    [entries, idPrefix, resources]
  );
  const wind = useMemo(() => buildWindOverlay(entries, resources), [entries, resources]);
  const weatherPanel = useMemo(
    () => buildWeatherPanel(entries, resources),
    [entries, resources]
  );
  const fuel3d = useMemo(
    () => buildFuel3DItems(entries, idPrefix, resources),
    [entries, idPrefix, resources]
  );

  const settled =
    pageSourcesSettled &&
    (isPublic ||
      (!treatmentsQuery.isPending &&
        (!regimeNeeded || !regimesQuery.isPending) &&
        (customIds.length === 0 ||
          (!customListQuery.isPending && !customFeaturesQuery.isPending)) &&
        !simQuery.isPending &&
        (footprintRequests.length === 0 || !footprintsQuery.isPending)));

  return { layers, settled, resources, wind, weatherPanel, fuel3d };
}
